// File: app/src/hooks/useWorkoutCoach.js
//
// React bridge for the compiled-queue WorkoutEngine. Owns the engine
// instance, synchronises engine state to React coachState on every
// transition, runs the 1-second phase timer and count-in tick, and
// exposes the navigation surface consumed by WorkoutContext.
//
// All tree traversal lives in the engine — this hook does only state
// synchronisation, lifecycle glue, and React-friendly callbacks.

import { useState, useCallback, useEffect, useRef } from "react";
import usePlanEditor from "./usePlanEditor";
import { WorkoutEngine, WORKOUT_ENGINE_STATUS } from "../engine/WorkoutEngine";
import { toSeconds } from "../utils/time";
import { amendReps, createPhaseLogEntries, doneRepsSets, lastRepsSet } from "../utils/phaseLog";
import { isRepUnit } from "../utils/planNodeUtils";
import {
  getWorkoutSettings,
  WORKOUT_SETTINGS_CHANGE_EVENT,
} from "./useWorkoutSettings";
import { getAudioSettings, setAudioMuted } from "./useAudioSettings";
import { MIDWAY_BEEP_MIN_SECONDS } from "../config/soundProfiles";
import { isNativeApp } from "../platform/native";
import {
  askBatteryExemptionOnce,
  buildCueTimeline,
  loadNativeCueSounds,
  onNativeCoachAction,
  stepTitle,
  stopNativeCoach,
  syncNativeCoach,
  watchAppForeground,
} from "../platform/nativeCoach";
import api from "../services/api";
import { appendFreeBlock, settleFreeBlock } from "../utils/keepGoing";
import { subscribeDeviceState } from "../services/settingsChannel";
import { fetchSessionWeather } from "../utils/sessionWeather";
import localStorageService from "../services/localStorageService";
import { findNodeByPath } from "../utils/workoutPlanUtils";
import { coerceLegacyNodeTypes } from "../utils/planNormaliser";
import {
  LOAD_DIMENSIONS,
  nodeLoadDimension,
  setNodeLoad,
  treePathToIndexPath,
} from "../utils/sessionLoads";
import { enclosingSetCount } from "../utils/planEditing";
import { freeTrainingRecord, isFreeTraining } from "../utils/freeTraining";
import {
  endRest,
  loggedCount,
  normaliseChecklist,
  normaliseLog,
  openRest,
  restStatus,
} from "../utils/freeTrainingLog";

// Walk an indexPath ([2, 0, 1] etc.) from the plan root to the addressed
// node. Returns the node, or undefined if the path doesn't resolve.
const INITIAL_COACH_STATE = {
  status: "ready",
  currentNode: null,
  nextUpNode: null,
  nextUpIsTimed: false,
  path: [],
  loopCounters: {},
  timeLeftInPhase: 0,
  // False on a counted (reps/cal) step: no countdown, but the session clock
  // still runs so the set's duration is logged.
  phaseIsTimed: false,
  progress: [],
  canGoBack: false,
  isLast: false,
  totalSessionDuration: 0,
  elapsedSessionTime: 0,
  phaseStartElapsed: 0,
  setStartElapsed: 0,
  repPhaseCumulativeActual: 0,
  countUpMode: false,
  isReadyGate: false,
  readyGateElapsed: 0,
  faffTotal: 0,
  // Free training: what you've logged, as you went (#157): plan steps, each
  // stamped with when it was logged. See utils/freeTrainingLog.
  freeLog: [],
  // Free training: the checklist jotted before Start (#164), a plan of its
  // own (null when there's none). It carries through Start; a new session
  // clears it.
  freeChecklist: null,
  // Free training: rests marked with the Rest button, on the session clock
  // ({ from, to, target }). Drawn on the dial; not saved with the session.
  freeRests: [],
  // Where Free training's clock starts on the session clock: 0, or when a
  // block kept going into after the finish began (#199). The log and rests
  // are stamped on Free training's clock.
  freeStart: 0,
};

const COACH_SESSION_STORAGE_KEY = "coachSessionState";
// "readyGate" is treated as active so a session paused mid-gate persists and
// restores (it is written to storage as "paused" — see the snapshot effect).
const ACTIVE_COACH_STATUSES = new Set(["running", "paused", "readyGate"]);

// Derive the concentric-progress ring items from an activeItem's tree path +
// loop counters. Mirrors the behaviour of the legacy advanceToNode so the
// CoachView doesn't need to change.
const computeProgressItems = (plan, activeItem) => {
  if (!plan || !activeItem) return [];
  const { sourceTreePath: path, loopCounters } = activeItem;
  const items = [];
  const tempPath = [];
  for (let i = 0; i < path.length; i += 2) {
    tempPath.push(path[i], path[i + 1]);
    const parentNode = findNodeByPath(plan, tempPath.slice(0, -2));
    if (parentNode?.type === "Group") {
      items.unshift({
        label: parentNode.label || parentNode.unit || "Items",
        current: (loopCounters[parentNode.id] || 0) + 1,
        total: parentNode.value,
        type: "group",
      });
    }
  }

  // Mark innermost group as rest-complete when the active node is a rest and
  // every remaining sibling is also a rest (i.e. the work for this set is
  // finished and only wind-down rests remain).
  if (items.length > 0 && activeItem.kind === "recovery") {
    const parentPath = path.slice(0, -2);
    const parentNode = findNodeByPath(plan, parentPath);
    if (parentNode?.type === "Group" && parentNode.children) {
      const childIdx = path[path.length - 1];
      const remaining = parentNode.children.slice(childIdx + 1);
      const allRest = remaining.every((c) =>
        (c.type || "").toLowerCase().includes("rest"),
      );
      if (allRest) items[0].restComplete = true;
    }
  }

  return items;
};

// Compute a stable "set key" for the innermost containing group, so we can
// detect when the active leaf crosses into a new set iteration and reset
// setStartElapsed accordingly.
const getSetKey = (activeItem) => {
  if (!activeItem) return "";
  const path = activeItem.sourceTreePath;
  if (!path || path.length < 2) return "";
  const parentPath = path.slice(0, -2);
  return `${parentPath.join(",")}#${JSON.stringify(activeItem.loopCounters)}`;
};

// Stable identity for the active queue position. EMOM-shaped plans reuse the
// same Work leaf object across every interval, so reference equality on the
// node is not enough to detect a phase change — path + loopCounters is.
/**
 * The telemetry summary to save. Heart rate from the Khronos device (polled
 * every 3 s) used to be summarised and then dropped; when no strap or machine
 * recorded heart rate, its summary becomes the session's hr block (#94).
 */
export const sessionTelemetrySummary = (telemetrySummary, deviceHr, env = null, weather = null, machines = null) => {
  let out = telemetrySummary || null;
  if (!telemetrySummary?.hr && deviceHr?.avg_bpm) {
    out = {
      ...(telemetrySummary || {}),
      hr: {
        avg: deviceHr.avg_bpm,
        min: deviceHr.min_bpm,
        max: deviceHr.max_bpm,
        source: "device",
        ...(deviceHr.zones ? { zones: deviceHr.zones } : {}),
      },
    };
  }
  // The Timer's lowest and highest temperature during the session, for the
  // weather badges (#170).
  if (env) out = { ...(out || {}), env };
  // The weather where you trained, when switched on in Settings → Phone.
  if (weather) out = { ...(out || {}), weather };
  // Which rower and bike it was on (api/lib/machines.js).
  if (machines) out = { ...(out || {}), machines };
  return out;
};

/**
 * Fold one temperature reading into the session's range. Returns the range
 * unchanged for a missing or non-numeric reading.
 * @param {{ temp_min_c: number, temp_max_c: number } | null} range
 * @param {unknown} tempC
 */
export const foldTemperature = (range, tempC) => {
  if (typeof tempC !== "number" || !Number.isFinite(tempC)) return range;
  const t = Math.round(tempC * 10) / 10;
  if (!range) return { temp_min_c: t, temp_max_c: t };
  return { temp_min_c: Math.min(range.temp_min_c, t), temp_max_c: Math.max(range.temp_max_c, t) };
};

const getPhaseKey = (activeItem) => {
  if (!activeItem) return "";
  return `${activeItem.sourceTreePath.join(",")}#${JSON.stringify(activeItem.loopCounters)}`;
};

// Why the user left the phase in progress. On a reps step, moving on IS
// finishing the set: the Coach's big Done button does it. It used to be
// logged as a skip, so every reps set looked skipped in the phase log
// (#92 phase 2).
const leavingCause = (engine) => {
  const state = engine.getCurrentState();
  const active = state?.activeItem;
  const node = state?.activeNode || active?.node;
  const repsStep = !active?.isTransition && active?.kind !== "recovery" && isRepUnit(node?.unit);
  return repsStep ? "done" : "skipNext";
};

const useWorkoutCoach = ({
  workoutPlan,
  setWorkoutPlan,
  playSound,
  isAuthenticated,
  setView,
  showToast,
  profile,
  historyMap,
  notePeakHrRef,
}) => {
  const engineRef = useRef(null);
  // Rep-estimate inputs, held in a ref rather than closed over: they refresh
  // whenever the profile syncs or a session is saved, and the callbacks that
  // build engines should not be rebuilt each time. Read at construction, which
  // is the only moment the queue is compiled.
  const estimateInputsRef = useRef({ profile: null, historyMap: null });
  estimateInputsRef.current = { profile, historyMap };
  const [coachState, setCoachState] = useState(() => ({
    ...INITIAL_COACH_STATE,
  }));
  const [sessionLoadedIntoCoach, setSessionLoadedIntoCoach] = useState(false);
  const [skipLastRest, setSkipLastRest] = useState(
    () => getWorkoutSettings().skipLastRest,
  );

  // Ref mirror so stable callbacks can read the freshest coachState without
  // re-binding every tick.
  const coachStateRef = useRef(coachState);
  coachStateRef.current = coachState;

  // Session lifecycle refs (preserved from legacy implementation).
  const sessionStartedAtRef = useRef(null);
  const activeProgrammeSessionRef = useRef(null);
  const phaseLogRef = useRef([]);
  // Bumped by each reps correction (#97): the log is a ref, so this is what
  // re-renders the Coach and re-persists the snapshot after one.
  const [repsEdits, setRepsEdits] = useState(0);
  const lastTransitionCauseRef = useRef("auto");
  const timerRef = useRef(null);
  const countInTickRef = useRef(null);
  const readyGateTickRef = useRef(null);
  const hrSummaryRef = useRef(null);
  // Temperature range the Timer reported during the session (#170).
  const envRef = useRef(null);
  const weatherRef = useRef(null);
  const bikeSummaryRef = useRef(null);
  // The rower and bike used, by type, saved in telemetry_summary.machines
  // (utils/ble/deviceInfo machinesUsed). Kept in sync by CoachView.
  const machinesRef = useRef(null);
  // 1 Hz telemetry array + computed summary. CoachView mirrors the recorder's
  // refs into these so the save path can attach them to the session payload.
  const telemetryRef = useRef(null);
  const telemetrySummaryRef = useRef(null);
  // The recorder's own session id — used to clear the IDB entry after a
  // successful server save (so a network failure can be recovered).
  const telemetrySessionIdRef = useRef(null);
  // Callback the recorder injects at workout end so the save flow can
  // finalize + clear without having to import the hook here.
  const telemetryFinalizeRef = useRef(null);
  const telemetryClearAfterSaveRef = useRef(null);
  // The recorder's rows so far, for the end-of-session calories before the
  // recording is finalised on save.
  const telemetryPeekRef = useRef(null);
  const hasResolvedPersistenceRef = useRef(false);
  const isEndingSessionRef = useRef(false);
  // Tracks the phase identity (or "__finished__") we last played a cue for, so
  // a dedicated effect can sound each phase / the completion exactly once even
  // if syncFromEngine commits the same transition twice.
  const lastSoundedPhaseRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Engine ↔ React synchronisation
  // ---------------------------------------------------------------------------

  // Pull the current engine state and project it onto coachState. Accepts an
  // optional `opts` object so individual navigation paths can tweak derived
  // fields (e.g. resetSetStart when a new round begins).
  // `planOverride` is for the caller that has just handed the engine a NEW plan
  // (applySessionEdit): `workoutPlan` here is still the pre-edit tree from this
  // callback's closure, so the progress rings would be computed against a shape
  // the engine has already moved past.
  const syncFromEngine = useCallback((planOverride) => {
    const engine = engineRef.current;
    if (!engine) return;
    const plan = planOverride || workoutPlan;
    const snap = engine.getCurrentState();

    setCoachState((prev) => {
      const activeItem = snap.activeItem;
      const activeNode = snap.activeNode;
      const prevNode = prev.currentNode;
      const prevPhaseKey = prev.__phaseKey || "";
      const newPhaseKey = getPhaseKey(activeItem);
      const phaseChanged = prevPhaseKey !== newPhaseKey;

      // Log the outgoing phase duration when the active leaf changes, or when
      // Finish ends the session on its last phase: engine.finish() leaves the
      // last item active, so the phase key doesn't change.
      const finished = snap.status === WORKOUT_ENGINE_STATUS.FINISHED;
      if (prevNode?.id && (phaseChanged || finished)) {
        const actualDuration = prev.elapsedSessionTime - prev.phaseStartElapsed;
        const cause = lastTransitionCauseRef.current || "auto";
        // "done" is finishing a reps step; it completes the set (#92).
        const skipped = cause !== "auto" && cause !== "done";
        phaseLogRef.current.push(...createPhaseLogEntries(prevNode, actualDuration, {
          completed: !skipped,
          skipped,
          skipReason: skipped ? cause : null,
        }));
        lastTransitionCauseRef.current = "auto";
      }

      // Session finished — the engine has no active item.
      if (finished || !activeItem) {
        return {
          ...prev,
          status: "finished",
          currentNode: null,
          nextUpNode: null,
          path: [],
          progress: prev.progress.map((p) => ({ ...p, current: p.total })),
          canGoBack: true,
          isLast: true,
          timeLeftInPhase: 0,
        };
      }

      // Map the engine's countIn status onto the React status shape.
      const engineStatus = snap.status;
      let reactStatus = "running";
      if (engineStatus === WORKOUT_ENGINE_STATUS.PAUSED) reactStatus = "paused";
      else if (engineStatus === WORKOUT_ENGINE_STATUS.COUNT_IN)
        reactStatus = "running";
      else if (engineStatus === WORKOUT_ENGINE_STATUS.READY_GATE)
        // Distinct status so the phase-countdown effect (gated on "running")
        // does not tick the clock during the Ready gate; a dedicated gate
        // effect runs the count-up instead.
        reactStatus = "readyGate";
      else if (engineStatus === WORKOUT_ENGINE_STATUS.READY)
        reactStatus = "ready";

      const newTimeLeft = activeItem.isTimed
        ? toSeconds(activeNode.value, activeNode.unit)
        : 0;

      // Detect set boundary for setStartElapsed.
      const prevKey = prev.currentNode ? prev.__setKey || "" : "";
      const newKey = getSetKey(activeItem);
      const setChanged = prevKey !== newKey;

      const wasRepBased =
        prev.currentNode && !["s", "m"].includes(prev.currentNode.unit);
      const repIncrement = wasRepBased
        ? Math.max(0, prev.elapsedSessionTime - prev.phaseStartElapsed)
        : 0;

      // Phase-transition cues are NOT played here — side effects inside a
      // state updater fire twice under StrictMode / re-run sync paths. A
      // dedicated effect watches the committed __phaseKey instead. We only
      // surface the derived data it needs (see __isSetRest below).
      const isSetRest = !!(
        activeItem.kind === "recovery" && activeItem.enclosingGroup?.isLastChild
      );

      return {
        ...prev,
        status: reactStatus,
        currentNode: activeNode,
        nextUpNode: snap.nextUpNode,
        nextUpIsTimed: snap.nextUpIsTimed,
        path: [...activeItem.sourceTreePath],
        loopCounters: { ...activeItem.loopCounters },
        timeLeftInPhase: phaseChanged ? newTimeLeft : prev.timeLeftInPhase,
        phaseIsTimed: !!activeItem.isTimed,
        progress: computeProgressItems(plan, activeItem),
        canGoBack: snap.canGoBack,
        isLast: snap.isLast,
        totalSessionDuration: snap.totalDuration,
        phaseStartElapsed: phaseChanged
          ? prev.elapsedSessionTime
          : prev.phaseStartElapsed,
        setStartElapsed: setChanged
          ? prev.elapsedSessionTime
          : prev.setStartElapsed,
        repPhaseCumulativeActual: prev.repPhaseCumulativeActual + repIncrement,
        isReadyGate: engineStatus === WORKOUT_ENGINE_STATUS.READY_GATE,
        readyGateElapsed: snap.readyGateElapsed,
        faffTotal: snap.faffTotal,
        __setKey: newKey,
        __phaseKey: newPhaseKey,
        __isSetRest: isSetRest,
      };
    });
    // playSound intentionally omitted — cues now fire from the dedicated
    // effect below, not inside this updater.
  }, [workoutPlan]);

  // Play phase-transition and completion cues from committed state — never
  // inside a setState updater. Ref-guarded so each phase / the finish fires
  // exactly once even if the same transition commits twice (StrictMode,
  // re-run sync paths). countdown_tick / halfway are mid-interval cues and
  // stay in the phase-countdown interval body (still outside its updater).
  useEffect(() => {
    if (coachState.status === "finished") {
      // The completion fanfare is deferred to the explicit Save of the session
      // (see finishWorkout) so a cancelled/discarded session never plays the
      // victory cue (issue #41). We still latch the guard so no stray phase cue
      // fires after finishing.
      lastSoundedPhaseRef.current = "__finished__";
      return;
    }
    if (coachState.status === "ready") {
      lastSoundedPhaseRef.current = null; // re-arm for the next session
      return;
    }

    const phaseKey = coachState.__phaseKey;
    const node = coachState.currentNode;
    if (!phaseKey || !node) return;
    if (lastSoundedPhaseRef.current === phaseKey) return;
    lastSoundedPhaseRef.current = phaseKey;

    const t = (node.type || "").toLowerCase();
    const isRecovery = t.includes("rest") || t === "transition";
    if (isRecovery) playSound(coachState.__isSetRest ? "set_rest" : "rest");
    else if (t.includes("warmup")) playSound("warmup");
    else if (t.includes("cooldown")) playSound("cooldown");
    else playSound("work");
  }, [
    coachState.status,
    coachState.__phaseKey,
    coachState.currentNode,
    coachState.__isSetRest,
    playSound,
  ]);

  // ---------------------------------------------------------------------------
  // Live skipLastRest reconfiguration
  // ---------------------------------------------------------------------------

  // Subscribe to cross-instance workout-settings updates so the coach's copy
  // of skipLastRest tracks the user's preference even when changed mid-session.
  useEffect(() => {
    const handler = (e) => {
      const next = e?.detail?.skipLastRest ?? getWorkoutSettings().skipLastRest;
      setSkipLastRest((prev) => (prev === next ? prev : next));
    };
    window.addEventListener(WORKOUT_SETTINGS_CHANGE_EVENT, handler);
    return () =>
      window.removeEventListener(WORKOUT_SETTINGS_CHANGE_EVENT, handler);
  }, []);

  // When skipLastRest changes, reconfigure the engine in-place and resync
  // React state so queue length and total duration reflect the new filter.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    if (engine.skipLastRest === skipLastRest) return;
    engine.updateConfig({ skipLastRest });
    syncFromEngine();
  }, [skipLastRest, syncFromEngine]);

  // ---------------------------------------------------------------------------
  // Count-in exposure (derived from engine on every render)
  // ---------------------------------------------------------------------------

  const [countInRemaining, setCountInRemaining] = useState(0);
  const [countInTotal, setCountInTotal] = useState(0);
  const [countInContext, setCountInContext] = useState(null);

  const syncCountInFromEngine = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) {
      setCountInRemaining(0);
      setCountInTotal(0);
      setCountInContext(null);
      return;
    }
    const snap = engine.getCurrentState();
    setCountInRemaining(snap.countInRemaining);
    setCountInTotal(snap.countInTotal);
    setCountInContext(
      snap.status === WORKOUT_ENGINE_STATUS.COUNT_IN ? snap.activeNode : null,
    );
  }, []);

  const restoreCoachStateFromSnapshot = useCallback(
    (snapshot) => {
      const engine = engineRef.current;
      if (!engine) return;

      const restored = engine.getCurrentState();
      const activeItem = restored.activeItem;
      const activeNode = restored.activeNode;

      if (!activeItem || !activeNode) {
        setCoachState({
          ...INITIAL_COACH_STATE,
          countUpMode: snapshot.countUpMode ?? !!workoutPlan?.countUpMode,
        });
        return;
      }

      const restoredStatus = "paused";

      setCoachState({
        ...INITIAL_COACH_STATE,
        status: restoredStatus,
        currentNode: activeNode,
        nextUpNode: restored.nextUpNode,
        nextUpIsTimed: restored.nextUpIsTimed,
        path: [...activeItem.sourceTreePath],
        loopCounters: { ...activeItem.loopCounters },
        timeLeftInPhase: activeItem.isTimed
          ? Math.max(
              0,
              Number.isFinite(snapshot.timeLeftInPhase)
                ? snapshot.timeLeftInPhase
                : toSeconds(activeNode.value, activeNode.unit),
            )
          : 0,
        phaseIsTimed: !!activeItem.isTimed,
        progress: computeProgressItems(workoutPlan, activeItem),
        canGoBack: restored.canGoBack,
        isLast: restored.isLast,
        totalSessionDuration: restored.totalDuration,
        elapsedSessionTime: Math.max(0, snapshot.elapsedSessionTime || 0),
        phaseStartElapsed: Math.max(0, snapshot.phaseStartElapsed || 0),
        setStartElapsed: Math.max(0, snapshot.setStartElapsed || 0),
        repPhaseCumulativeActual: Math.max(
          0,
          snapshot.repPhaseCumulativeActual || 0,
        ),
        faffTotal: Math.max(0, snapshot.faffTotal || 0),
        countUpMode: snapshot.countUpMode ?? !!workoutPlan?.countUpMode,
        // Stored the old way (before the log was plan steps): converted.
        freeLog: Array.isArray(snapshot.freeLog) ? normaliseLog(snapshot.freeLog) : [],
        freeChecklist: normaliseChecklist(snapshot.freeChecklist),
        freeRests: Array.isArray(snapshot.freeRests) ? snapshot.freeRests : [],
        freeStart: Math.max(0, snapshot.freeStart || 0),
        __setKey: getSetKey(activeItem),
        __phaseKey: getPhaseKey(activeItem),
        __isSetRest: !!(
          activeItem.kind === "recovery" &&
          activeItem.enclosingGroup?.isLastChild
        ),
      });

      // Seed the cue guard so resuming a saved paused session doesn't replay
      // the phase cue for the phase we just restored into.
      lastSoundedPhaseRef.current = getPhaseKey(activeItem);
    },
    [workoutPlan],
  );

  useEffect(() => {
    if (hasResolvedPersistenceRef.current) return;

    if (!workoutPlan) {
      localStorageService.remove(COACH_SESSION_STORAGE_KEY);
      hasResolvedPersistenceRef.current = true;
      return;
    }

    const snapshot = localStorageService.get(COACH_SESSION_STORAGE_KEY);
    if (!snapshot?.sessionLoadedIntoCoach) {
      hasResolvedPersistenceRef.current = true;
      return;
    }

    clearInterval(timerRef.current);
    clearInterval(countInTickRef.current);
    clearInterval(readyGateTickRef.current);
    hrSummaryRef.current = null;
    envRef.current = null;
    weatherRef.current = null;
    bikeSummaryRef.current = null;
    machinesRef.current = null;
    telemetryRef.current = null;
    telemetrySummaryRef.current = null;
    telemetrySessionIdRef.current = null;

    const restoredSkipLastRest =
      typeof snapshot.skipLastRest === "boolean"
        ? snapshot.skipLastRest
        : getWorkoutSettings().skipLastRest;

    setSkipLastRest(restoredSkipLastRest);
    sessionStartedAtRef.current = snapshot.sessionStartedAt || null;
    activeProgrammeSessionRef.current = snapshot.activeProgrammeSession || null;
    phaseLogRef.current = Array.isArray(snapshot.phaseLog)
      ? snapshot.phaseLog
      : [];

    if (
      ACTIVE_COACH_STATUSES.has(snapshot.status) &&
      Number.isFinite(snapshot.currentIndex)
    ) {
      const engine = new WorkoutEngine(workoutPlan, {
        skipLastRest: restoredSkipLastRest,
        ...estimateInputsRef.current,
      });

      engine.restoreState({
        currentIndex: snapshot.currentIndex,
        status: WORKOUT_ENGINE_STATUS.PAUSED,
        countInRemaining: snapshot.countInRemaining,
        countInTotal: snapshot.countInTotal,
        faffTotal: snapshot.faffTotal,
      });

      engineRef.current = engine;
      restoreCoachStateFromSnapshot(snapshot);
      setSessionLoadedIntoCoach(true);
      syncCountInFromEngine();
      hasResolvedPersistenceRef.current = true;
      return;
    }

    engineRef.current = null;
    setCoachState({
      ...INITIAL_COACH_STATE,
      countUpMode: snapshot.countUpMode ?? !!workoutPlan?.countUpMode,
      // A checklist jotted on the start screen survives a reload (#164).
      freeChecklist: normaliseChecklist(snapshot.freeChecklist),
    });
    setSessionLoadedIntoCoach(true);
    syncCountInFromEngine();
    hasResolvedPersistenceRef.current = true;
  }, [restoreCoachStateFromSnapshot, syncCountInFromEngine, workoutPlan]);

  // Keep the Timer's temperature range while a session runs (#170). It only
  // grows, so a pause or a reload mid-session doesn't lose the readings.
  const sessionActive = ACTIVE_COACH_STATUSES.has(coachState.status);
  useEffect(() => {
    if (!sessionActive) return undefined;
    return subscribeDeviceState((evt) => {
      envRef.current = foldTemperature(envRef.current, evt?.env?.temperature_c);
    });
  }, [sessionActive]);

  // The weather where you're training, looked up once as the session starts
  // so Save never waits on it. Off unless switched on in Settings → Phone.
  useEffect(() => {
    if (!sessionActive || weatherRef.current || !getWorkoutSettings().weatherOnSessions) return undefined;
    let cancelled = false;
    fetchSessionWeather().then((weather) => {
      if (!cancelled && weather) weatherRef.current = weather;
    });
    return () => {
      cancelled = true;
    };
  }, [sessionActive]);

  useEffect(() => {
    if (!hasResolvedPersistenceRef.current) return;

    if (!workoutPlan) {
      localStorageService.remove(COACH_SESSION_STORAGE_KEY);
      return;
    }

    if (
      !sessionLoadedIntoCoach &&
      !ACTIVE_COACH_STATUSES.has(coachState.status)
    ) {
      localStorageService.remove(COACH_SESSION_STORAGE_KEY);
      return;
    }

    const engineState = engineRef.current?.getCurrentState();

    localStorageService.set(COACH_SESSION_STORAGE_KEY, {
      version: 1,
      sessionLoadedIntoCoach: true,
      // Persist a mid-gate session as "paused": on restore it comes back paused
      // and resuming arms the count-in into the timed phase (the in-progress
      // gate count-up is dropped, but faffTotal is preserved).
      status:
        coachState.status === "readyGate"
          ? "paused"
          : ACTIVE_COACH_STATUSES.has(coachState.status)
            ? coachState.status
            : "ready",
      currentIndex: engineState?.currentIndex ?? 0,
      countInRemaining,
      countInTotal,
      elapsedSessionTime: coachState.elapsedSessionTime,
      timeLeftInPhase: coachState.timeLeftInPhase,
      phaseStartElapsed: coachState.phaseStartElapsed,
      setStartElapsed: coachState.setStartElapsed,
      repPhaseCumulativeActual: coachState.repPhaseCumulativeActual,
      faffTotal: coachState.faffTotal,
      countUpMode: coachState.countUpMode,
      freeLog: coachState.freeLog,
      freeChecklist: coachState.freeChecklist,
      freeRests: coachState.freeRests,
      freeStart: coachState.freeStart,
      sessionStartedAt: sessionStartedAtRef.current,
      activeProgrammeSession: activeProgrammeSessionRef.current,
      phaseLog: phaseLogRef.current,
      skipLastRest,
    });
  }, [
    workoutPlan,
    sessionLoadedIntoCoach,
    coachState.status,
    coachState.elapsedSessionTime,
    coachState.timeLeftInPhase,
    coachState.phaseStartElapsed,
    coachState.setStartElapsed,
    coachState.repPhaseCumulativeActual,
    coachState.faffTotal,
    coachState.countUpMode,
    // An entry logged while paused must survive a reload too.
    coachState.freeLog,
    coachState.freeChecklist,
    coachState.freeRests,
    coachState.freeStart,
    countInRemaining,
    countInTotal,
    skipLastRest,
    // A correction must survive a reload even while paused, when nothing
    // else here changes (#144).
    repsEdits,
  ]);

  // ---------------------------------------------------------------------------
  // Timer effects — count-in tick and phase countdown
  // ---------------------------------------------------------------------------

  // Count-in tick: fires once per second while the engine is in COUNT_IN.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return undefined;
    if (coachState.status !== "running" || countInRemaining <= 0)
      return undefined;
    if (engine.status !== WORKOUT_ENGINE_STATUS.COUNT_IN) return undefined;

    playSound("countdown_tick");
    countInTickRef.current = setInterval(() => {
      const done = engine.tickCountIn();
      syncCountInFromEngine();
      if (done) {
        clearInterval(countInTickRef.current);
        syncFromEngine();
      }
    }, 1000);
    return () => clearInterval(countInTickRef.current);
  }, [
    coachState.status,
    countInRemaining,
    playSound,
    syncCountInFromEngine,
    syncFromEngine,
  ]);

  // ---------------------------------------------------------------------------
  // Catching up after the app was in the background (Android app only)
  // ---------------------------------------------------------------------------

  // Wall-clock time of the phase countdown's last tick, or null while it isn't
  // ticking. Android throttles a hidden page's timers to once a minute, so in
  // the app a tick can arrive long after the second it was for; the clock then
  // runs on by the seconds it missed (fastForward). The web is unchanged.
  const lastTickAtRef = useRef(null);
  const catchUpEnabled = isNativeApp();

  // Run the clock on by `seconds` without a sound: CoachService played the
  // cues meanwhile. Moves through every timed phase that ran out, logging
  // each as the tick would have, and stops at a counted step or the end.
  const fastForward = useCallback(
    (seconds) => {
      const engine = engineRef.current;
      if (!engine || !(seconds > 0)) return;
      let left = Math.floor(seconds);
      let timeLeft = coachStateRef.current.timeLeftInPhase;
      const countUp = !!coachStateRef.current.countUpMode;
      let moved = false;
      for (let guard = 0; left > 0 && guard < 10000; guard++) {
        const snap = engine.getCurrentState();
        if (snap.status !== WORKOUT_ENGINE_STATUS.RUNNING || !snap.activeItem) break;
        if (countUp || !snap.activeItem.isTimed) {
          const all = left;
          setCoachState((prev) => ({ ...prev, elapsedSessionTime: prev.elapsedSessionTime + all }));
          left = 0;
          break;
        }
        const step = Math.min(left, Math.max(0, timeLeft));
        left -= step;
        timeLeft -= step;
        setCoachState((prev) => ({
          ...prev,
          elapsedSessionTime: prev.elapsedSessionTime + step,
          timeLeftInPhase: Math.max(0, prev.timeLeftInPhase - step),
        }));
        if (timeLeft > 0) break;
        lastTransitionCauseRef.current = "auto";
        engine.autoAdvance();
        syncFromEngine();
        moved = true;
        const next = engine.getCurrentState();
        if (next.status !== WORKOUT_ENGINE_STATUS.RUNNING || !next.activeItem) break;
        timeLeft = next.activeItem.isTimed
          ? toSeconds(next.activeNode.value, next.activeNode.unit)
          : 0;
      }
      if (moved) {
        // The service already played this phase's opening cue.
        const now = engine.getCurrentState();
        lastSoundedPhaseRef.current =
          now.status === WORKOUT_ENGINE_STATUS.FINISHED ? "__finished__" : getPhaseKey(now.activeItem);
        syncCountInFromEngine();
      }
    },
    [syncFromEngine, syncCountInFromEngine],
  );

  // Phase countdown: 1-second tick for the active timed phase. A counted
  // (reps/cal) step has no countdown, but the session clock keeps running
  // through it: the set's logged duration is what marks it done for
  // progression and the reps correction (#97), and what rep-pace history
  // divides by.
  useEffect(() => {
    const isCountUp = coachState.countUpMode;
    const isUntimedPhase = !!coachState.currentNode && !coachState.phaseIsTimed;
    const shouldTick =
      coachState.status === "running" &&
      countInRemaining <= 0 &&
      (isCountUp || isUntimedPhase || coachState.timeLeftInPhase > 0);

    if (!shouldTick) {
      clearInterval(timerRef.current);
      lastTickAtRef.current = null;
      return undefined;
    }
    if (lastTickAtRef.current == null) lastTickAtRef.current = Date.now();

    timerRef.current = setInterval(() => {
      if (catchUpEnabled) {
        const nowMs = Date.now();
        const lag = nowMs - (lastTickAtRef.current ?? nowMs);
        lastTickAtRef.current = nowMs;
        if (lag >= 2500 && coachStateRef.current.status === "running") {
          // This tick is seconds late: run the clock on to now, quietly.
          clearInterval(timerRef.current);
          fastForward(Math.round(lag / 1000));
          return;
        }
      }
      // Decide cues from the freshest committed snapshot and fire them from
      // the interval body — never inside the setCoachState updater (a state
      // updater must be pure; side effects there double under StrictMode /
      // re-runs). The interval is the sole writer of timeLeftInPhase per tick,
      // so coachStateRef.current is authoritative here.
      const snapshot = coachStateRef.current;
      if (snapshot.status !== "running") {
        clearInterval(timerRef.current);
        return;
      }
      if (!snapshot.countUpMode && snapshot.phaseIsTimed) {
        const newTime = snapshot.timeLeftInPhase - 1;
        if (newTime < 1) {
          clearInterval(timerRef.current);
          playSound("countdown_tick");
          // Defer auto-advance so the final state update renders first.
          setTimeout(() => {
            const engine = engineRef.current;
            if (!engine) return;
            engine.autoAdvance();
            syncFromEngine();
            syncCountInFromEngine();
          }, 100);
        } else {
          if (newTime <= 3) playSound("countdown_tick");
          if (snapshot.currentNode) {
            const totalPhase = toSeconds(
              snapshot.currentNode.value,
              snapshot.currentNode.unit,
            );
            if (
              totalPhase > MIDWAY_BEEP_MIN_SECONDS &&
              newTime === Math.floor(totalPhase / 2)
            ) {
              playSound("halfway");
            }
          }
        }
      }

      // Free training's timed rest: the last three seconds, then the cue to
      // go again. An open rest just counts up.
      const rest = restStatus(
        openRest(snapshot.freeRests),
        (snapshot.elapsedSessionTime || 0) - (snapshot.freeStart || 0) + 1,
      );
      if (rest?.left != null) {
        if (rest.left > 0 && rest.left <= 3) playSound("countdown_tick");
        else if (rest.left === 0) playSound("work");
      }

      setCoachState((prev) => {
        if (prev.status !== "running") {
          clearInterval(timerRef.current);
          return prev;
        }
        // Count-up mode and counted steps: stopwatch behaviour, no phase
        // transitions.
        if (prev.countUpMode || !prev.phaseIsTimed) {
          return { ...prev, elapsedSessionTime: prev.elapsedSessionTime + 1 };
        }
        const newTime = prev.timeLeftInPhase - 1;
        const newElapsed = prev.elapsedSessionTime + 1;
        return {
          ...prev,
          timeLeftInPhase: Math.max(0, newTime),
          elapsedSessionTime: newElapsed,
        };
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [
    coachState.status,
    coachState.timeLeftInPhase,
    coachState.countUpMode,
    coachState.currentNode,
    coachState.phaseIsTimed,
    countInRemaining,
    playSound,
    syncFromEngine,
    syncCountInFromEngine,
    catchUpEnabled,
    fastForward,
  ]);

  // Ready-gate count-up: while the engine is in the gate, tick the faff timer
  // and keep the REAL session clock running (the gate is wall-clock time the
  // user spends getting to the next station). The engine caps the gate and
  // auto-pauses if the user walks away.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return undefined;
    if (coachState.status !== "readyGate") return undefined;

    readyGateTickRef.current = setInterval(() => {
      const eng = engineRef.current;
      if (!eng) return;
      const capped = eng.tickReadyGate();
      setCoachState((prev) => ({
        ...prev,
        elapsedSessionTime: prev.elapsedSessionTime + 1,
        readyGateElapsed: eng.readyGateElapsed,
        faffTotal: eng.faffTotal,
      }));
      if (capped) {
        clearInterval(readyGateTickRef.current);
        syncFromEngine(); // reflect the auto-pause
      }
    }, 1000);
    return () => clearInterval(readyGateTickRef.current);
  }, [coachState.status, syncFromEngine]);

  // ---------------------------------------------------------------------------
  // Navigation callbacks — thin wrappers around the engine
  // ---------------------------------------------------------------------------

  const withCountIn = () => ({
    countInSeconds: getAudioSettings().countInDuration,
    changeoverHold: getWorkoutSettings().changeoverHold,
  });

  const confirmReady = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    lastTransitionCauseRef.current = "auto";
    engine.confirmReady(withCountIn());
    syncFromEngine();
    syncCountInFromEngine();
  }, [syncFromEngine, syncCountInFromEngine]);

  const skipToNext = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    lastTransitionCauseRef.current = leavingCause(engine);
    engine.skipNext(withCountIn());
    syncFromEngine();
    syncCountInFromEngine();
  }, [syncFromEngine, syncCountInFromEngine]);

  const skipToPrevious = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    lastTransitionCauseRef.current = "skipPrevious";
    engine.skipPrevious();
    syncFromEngine();
    syncCountInFromEngine();
  }, [syncFromEngine, syncCountInFromEngine]);

  const skipRoundForward = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    lastTransitionCauseRef.current = "skipRoundForward";
    engine.skipRoundForward(withCountIn());
    syncFromEngine();
    syncCountInFromEngine();
  }, [syncFromEngine, syncCountInFromEngine]);

  const skipRoundBack = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    lastTransitionCauseRef.current = "skipRoundBack";
    engine.skipRoundBack(withCountIn());
    syncFromEngine();
    syncCountInFromEngine();
  }, [syncFromEngine, syncCountInFromEngine]);

  // ---------------------------------------------------------------------------
  // Session lifecycle
  // ---------------------------------------------------------------------------

  const startWorkout = useCallback(() => {
    if (!workoutPlan) return;
    const { skipLastRest } = getWorkoutSettings();
    engineRef.current = new WorkoutEngine(workoutPlan, {
      skipLastRest,
      ...estimateInputsRef.current,
    });
    setSkipLastRest(skipLastRest);
    sessionStartedAtRef.current = new Date().toISOString();
    phaseLogRef.current = [];

    setCoachState((prev) => ({
      ...INITIAL_COACH_STATE,
      countUpMode: !!workoutPlan?.countUpMode,
      totalSessionDuration: engineRef.current.totalDuration,
      // Jotted on the start screen, ticked off once running (#164).
      freeChecklist: prev.freeChecklist || null,
    }));

    engineRef.current.start(withCountIn());
    syncFromEngine();
    syncCountInFromEngine();
  }, [workoutPlan, syncFromEngine, syncCountInFromEngine]);

  const playPause = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;

    const snap = engine.getCurrentState();
    if (snap.status === WORKOUT_ENGINE_STATUS.FINISHED || !snap.activeItem)
      return;

    if (coachStateRef.current.status === "running") {
      engine.pause();
      playSound("pause");
    } else {
      engine.resume(withCountIn());
      playSound("resume");
    }
    setCoachState((prev) => ({
      ...prev,
      status:
        engine.status === WORKOUT_ENGINE_STATUS.PAUSED ? "paused" : "running",
    }));
    syncCountInFromEngine();
  }, [playSound, syncCountInFromEngine]);

  // ---------------------------------------------------------------------------
  // The Coach in the background (Android app only, platform/nativeCoach.js)
  // ---------------------------------------------------------------------------

  // Tell CoachService what's to come whenever the phase, the status or the
  // count-in changes. In a browser it is never called.
  const nativeCoachOnRef = useRef(false);
  const workoutTitle = workoutPlan?.label;
  // Bumped as the app goes to the background, so the service gets the times
  // from this moment's clock rather than the phase start's.
  const [backgroundEpoch, setBackgroundEpoch] = useState(0);
  useEffect(() => {
    if (!catchUpEnabled) return;
    const engine = engineRef.current;
    const state = coachStateRef.current;
    const status = state.status;
    if (!engine || !sessionLoadedIntoCoach || status === "ready" || status === "finished") {
      if (nativeCoachOnRef.current) {
        nativeCoachOnRef.current = false;
        stopNativeCoach();
      }
      return;
    }
    const audio = getAudioSettings();
    const now = Date.now();
    const snap = engine.getCurrentState();
    const { cues, steps } = buildCueTimeline({
      queue: engine.queue,
      currentIndex: snap.currentIndex,
      engineStatus: snap.status,
      countInRemaining: snap.countInRemaining,
      timeLeftInPhase: state.timeLeftInPhase,
      countUpMode: state.countUpMode,
      countdownEnabled: audio.countdownEnabled,
      now,
    });
    const paused = status !== "running";
    const shown = steps.length
      ? steps
      : [
          {
            at: now,
            endsAt: null,
            title: stepTitle(snap.activeNode),
            text: status === "readyGate" ? "Tap Ready when you're set" : "Paused",
          },
        ];
    nativeCoachOnRef.current = true;
    syncNativeCoach({
      workoutTitle,
      cues,
      steps: shown,
      paused,
      volume: audio.volume,
      muted: audio.muted,
    });
  }, [
    catchUpEnabled,
    sessionLoadedIntoCoach,
    coachState.status,
    coachState.__phaseKey,
    coachState.currentNode,
    countInRemaining,
    workoutTitle,
    backgroundEpoch,
  ]);

  // Leaving the Coach altogether stops the service.
  useEffect(
    () => () => {
      if (nativeCoachOnRef.current) {
        nativeCoachOnRef.current = false;
        stopNativeCoach();
      }
    },
    [],
  );

  // Pause, Resume and Skip from the notification. `at` is when it was
  // pressed: the clock catches up to then first, as the page may have been
  // throttled.
  const nativeActionRef = useRef(null);
  nativeActionRef.current = ({ action, at }) => {
    const state = coachStateRef.current;
    if (state.status === "running" && lastTickAtRef.current != null && at) {
      const behind = Math.floor((Number(at) - lastTickAtRef.current) / 1000);
      if (behind >= 1) {
        lastTickAtRef.current += behind * 1000;
        fastForward(behind);
      }
    }
    if (action === "pause" && coachStateRef.current.status === "running") playPause();
    else if (action === "resume" && coachStateRef.current.status === "paused") playPause();
    else if (action === "skip") skipToNext();
    else if (action === "mute" || action === "unmute") {
      setAudioMuted(action === "mute");
      setBackgroundEpoch((n) => n + 1); // re-send with the new state
    }
  };
  useEffect(() => {
    if (!catchUpEnabled) return undefined;
    const unwatch = watchAppForeground((active) => {
      if (!active) setBackgroundEpoch((n) => n + 1);
    });
    const unlisten = onNativeCoachAction((e) => nativeActionRef.current?.(e));
    return () => {
      unwatch();
      unlisten();
    };
  }, [catchUpEnabled]);

  // Ready the service's sounds (the user's set) and, once ever, ask to be
  // let off battery optimisation, when a workout starts in the app.
  const coachRunning = coachState.status === "running";
  useEffect(() => {
    if (!catchUpEnabled || !coachRunning) return;
    loadNativeCueSounds(getAudioSettings().profile);
    askBatteryExemptionOnce();
  }, [catchUpEnabled, coachRunning]);

  const persistCompletedSession = useCallback(
    async (feedback = {}) => {
      if (isEndingSessionRef.current) return;
      isEndingSessionRef.current = true;

      const endedAt = new Date().toISOString();
      const startedAt = sessionStartedAtRef.current || endedAt;
      const cs = coachStateRef.current;
      const durationMinutes =
        Math.round((cs.elapsedSessionTime || 0) / 60) || 1;
      const workoutType = workoutPlan?.label || workoutPlan?.type || "Workout";
      const rating = Number.isFinite(feedback?.rpe) ? feedback.rpe : null;
      const notes =
        typeof feedback?.notes === "string" && feedback.notes.trim()
          ? feedback.notes.trim()
          : undefined;

      // Log the in-progress phase if the workout was stopped mid-phase.
      if (cs.currentNode?.id) {
        const actualDuration = cs.elapsedSessionTime - cs.phaseStartElapsed;
        phaseLogRef.current.push(...createPhaseLogEntries(cs.currentNode, actualDuration, {
          completed: false,
          skipped: false,
          skipReason: "sessionEnded",
        }));
      }

      const planDataWithLog = workoutPlan
        ? { ...workoutPlan, phase_log: phaseLogRef.current }
        : workoutPlan;

      // Finalize the telemetry recorder if it's wired up — flushes the last
      // IDB write and computes the per-device summary. Failure here must NOT
      // block the session save; telemetry is auxiliary data.
      if (telemetryFinalizeRef.current) {
        try {
          const result = await telemetryFinalizeRef.current();
          if (result) {
            telemetryRef.current = result.samples;
            telemetrySummaryRef.current = result.summary;
            telemetrySessionIdRef.current = result.sessionId;
          }
        } catch (err) {
          console.warn("[Khronos] telemetry finalize failed", err);
        }
      }

      // Free training's plan was only a stopwatch: it's filed as the activity
      // picked at the end, with what was logged as its plan (#146, #157).
      const freeRecord = isFreeTraining(workoutPlan)
        ? freeTrainingRecord({
            activityType: feedback?.activityType,
            log: cs.freeLog || [],
            plan: feedback?.freePlan,
          })
        : null;

      const sessionData = {
        started_at: startedAt,
        ended_at: endedAt,
        duration_minutes: durationMinutes,
        workout_type: workoutType,
        plan_data: planDataWithLog,
        ...(freeRecord
          ? {
              workout_type: freeRecord.workout_type,
              plan_data: freeRecord.plan_data,
            }
          : {}),
        notes,
        rating,
        ...(bikeSummaryRef.current
          ? { bike_summary: bikeSummaryRef.current }
          : {}),
        ...(telemetryRef.current && telemetryRef.current.length > 0
          ? { telemetry: telemetryRef.current }
          : {}),
        ...(sessionTelemetrySummary(telemetrySummaryRef.current, hrSummaryRef.current, envRef.current, weatherRef.current, machinesRef.current)
          ? {
              telemetry_summary: sessionTelemetrySummary(
                telemetrySummaryRef.current,
                hrSummaryRef.current,
                envRef.current,
                weatherRef.current,
                machinesRef.current,
              ),
            }
          : {}),
        // Total time spent in rep→timed "Ready gates" — telemetry for tuning
        // the transition-time defaults from real movement data.
        ...(coachStateRef.current?.faffTotal
          ? { faff_seconds: coachStateRef.current.faffTotal }
          : {}),
      };

      const saveToLocalBackup = () => {
        try {
          const stored = JSON.parse(
            localStorage.getItem("completedSessions") || "[]",
          );
          stored.unshift({
            ...sessionData,
            id: `local-${Date.now()}`,
            created_at: endedAt,
          });
          if (stored.length > 100) stored.length = 100;
          localStorage.setItem("completedSessions", JSON.stringify(stored));
        } catch (storageErr) {
          console.error(
            "[Khronos] Failed to write local session backup:",
            storageErr,
          );
        }
      };

      let savedSession = null;
      try {
        if (isAuthenticated) {
          savedSession = await api.createSession(sessionData);
          const progSession = activeProgrammeSessionRef.current;
          if (progSession && savedSession?.id) {
            try {
              await api.completeProgrammeSession(
                progSession.programmeId,
                progSession.programmeSessionId,
                {
                  workout_session_id: savedSession.id,
                  duration_minutes: durationMinutes,
                },
              );
            } catch (err) {
              console.error(
                "[Khronos] Failed to update programme session:",
                err,
              );
            }
            activeProgrammeSessionRef.current = null;
          }
        } else {
          saveToLocalBackup();
        }
      } catch (err) {
        console.error("[Khronos] Failed to save session:", err);
        saveToLocalBackup();
        showToast?.({
          title: "Session saved locally",
          description:
            "Couldn't reach the server — we've kept a local copy and will sync later.",
        });
      } finally {
        // By this point the session is persisted somewhere — a network save,
        // or a local backup (which already embeds the telemetry). Either way
        // the IndexedDB in-flight copy is now redundant, so drop it so it
        // doesn't resurface as an "interrupted workout" on the next load.
        // The crash-recovery banner is only for sessions that never reached
        // this save path at all (tab killed mid-workout).
        notePeakHrRef?.current?.(telemetryRef.current);
        if (telemetryClearAfterSaveRef.current) {
          try {
            await telemetryClearAfterSaveRef.current();
          } catch (err) {
            console.warn("[Khronos] telemetry IDB cleanup failed", err);
          }
        }
        sessionStartedAtRef.current = null;
        hrSummaryRef.current = null;
        envRef.current = null;
        weatherRef.current = null;
        bikeSummaryRef.current = null;
        machinesRef.current = null;
        telemetryRef.current = null;
        telemetrySummaryRef.current = null;
        telemetrySessionIdRef.current = null;
        engineRef.current?.finish();
        engineRef.current = null;

        setCoachState({ ...INITIAL_COACH_STATE });
        setSessionLoadedIntoCoach(false);
        syncCountInFromEngine();
        setView("session");
        isEndingSessionRef.current = false;
      }
      return savedSession;
    },
    [workoutPlan, isAuthenticated, setView, syncCountInFromEngine, showToast, notePeakHrRef],
  );

  const finishWorkout = useCallback(
    (feedback) => {
      // Fire the victory fanfare here — on the explicit Save of a completed
      // session — rather than on entering the "finished" state, so discarding
      // from the completion screen stays silent (issue #41). Confetti is gated
      // the same way inside SessionCompleteModal. Guarded by the same ref as
      // the save itself: a double-tapped Save button arrives well outside the
      // sound dedup window, and without this the fanfare doubled (issue #4).
      if (!isEndingSessionRef.current) playSound("end");
      return persistCompletedSession(feedback);
    },
    [persistCompletedSession, playSound],
  );

  // End the session WITHOUT saving: flip the engine to finished so the
  // completion modal (Save to History / Discard) owns the persistence
  // decision — the same flow as a natural finish.
  const completeWorkout = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    // Finish on the last phase logs it like moving on would: done on a reps
    // step, a skip on a timed one cut short.
    lastTransitionCauseRef.current = leavingCause(engine);
    engine.finish();
    syncFromEngine();
    syncCountInFromEngine();
  }, [syncFromEngine, syncCountInFromEngine]);

  // Keep going (#199): the finished session carries on, in a Free training
  // block added to the end of its plan, on the same clock and recording. The
  // plan is settled first, so a block kept going into before keeps its log.
  const keepGoing = useCallback(() => {
    const engine = engineRef.current;
    if (!engine || engine.status !== WORKOUT_ENGINE_STATUS.FINISHED) return false;
    const { plan: next, index } = appendFreeBlock(workoutPlan);
    if (!engine.extend(next, index)) return false;
    setWorkoutPlan(next);
    setCoachState((prev) => ({
      ...prev,
      // Runs until you finish it, like Free training on its own.
      countUpMode: true,
      freeLog: [],
      freeRests: [],
      freeStart: prev.elapsedSessionTime || 0,
    }));
    syncFromEngine(next);
    syncCountInFromEngine();
    return true;
  }, [workoutPlan, setWorkoutPlan, syncFromEngine, syncCountInFromEngine]);

  // At the finish, a Free training block's log becomes its steps, so the
  // finish screen shows and saves what was done in it (#199).
  useEffect(() => {
    if (coachState.status !== "finished") return;
    const served = (coachState.elapsedSessionTime || 0) - (coachState.freeStart || 0);
    const settled = settleFreeBlock(workoutPlan, coachState.freeLog, served);
    if (settled === workoutPlan) return;
    setWorkoutPlan(settled);
    setCoachState((prev) => ({ ...prev, freeLog: [], freeRests: [], countUpMode: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachState.status, workoutPlan]);

  // Free training (#157): change the log. `change(log, now)` returns the new
  // log; `now` is the session clock, which stamps anything logged.
  // Logging something ends a rest. `at` overrides the clock, for a session a
  // Khronos Timer is timing (the local clock stands still then).
  const updateFreeLog = useCallback((change, at) => {
    setCoachState((prev) => {
      const now = at ?? (prev.elapsedSessionTime || 0) - (prev.freeStart || 0);
      const next = change(prev.freeLog || [], now);
      if (next === prev.freeLog) return prev;
      const logged = loggedCount(next) > loggedCount(prev.freeLog || []);
      const rests = prev.freeRests || [];
      return {
        ...prev,
        freeLog: next,
        freeRests: logged && openRest(rests) ? endRest(rests, now) : rests,
      };
    });
  }, []);

  // Free training's rests: `change(rests, now)` returns the new list. `at`
  // overrides the clock, as for updateFreeLog.
  const updateFreeRests = useCallback((change, at) => {
    setCoachState((prev) => {
      const next = change(
        prev.freeRests || [],
        at ?? (prev.elapsedSessionTime || 0) - (prev.freeStart || 0),
      );
      return next === prev.freeRests ? prev : { ...prev, freeRests: next };
    });
  }, []);

  // Free training's checklist (#164), a plan: `change(plan)` returns the new
  // one. It's a plan setter, so usePlanEditor can drive it.
  const updateFreeChecklist = useCallback((change) => {
    setCoachState((prev) => {
      const next = change(prev.freeChecklist || null);
      return next === prev.freeChecklist ? prev : { ...prev, freeChecklist: next };
    });
  }, []);

  const savePartialSession = useCallback(
    (feedback) => persistCompletedSession(feedback),
    [persistCompletedSession],
  );

  // ---------------------------------------------------------------------------
  // Session loading
  // ---------------------------------------------------------------------------

  const loadSessionIntoCoach = useCallback(
    (planOverride) => {
      // The programme session panel passes the (possibly edited) plan directly
      // so it can save-and-run in one step. ActiveState wires this to an
      // onClick, so guard against being handed a click event.
      const override =
        planOverride && Array.isArray(planOverride.children)
          ? planOverride
          : null;
      const plan = override || workoutPlan;
      if (!plan) return;
      if (override && override !== workoutPlan) setWorkoutPlan(override);
      // #59: if this session belongs to a programme slot, persist the plan as
      // it stands right now (the user may have just edited it) back to the slot
      // so the programme reflects the change even before/without completing the
      // workout. Fire-and-forget; the programme view reconciles on next fetch.
      const prog = activeProgrammeSessionRef.current;
      if (isAuthenticated && prog?.programmeId && prog?.programmeSessionId) {
        api
          .assignProgrammeSession(prog.programmeId, prog.programmeSessionId, {
            label: plan.label || prog.label || prog.scheduledLabel || null,
            plan_data: plan,
          })
          .catch((err) =>
            console.error("[Programme] Failed to persist pre-run edits:", err),
          );
      }
      clearInterval(timerRef.current);
      clearInterval(countInTickRef.current);
      engineRef.current = null;
      sessionStartedAtRef.current = null;
      phaseLogRef.current = [];
      hrSummaryRef.current = null;
      envRef.current = null;
      weatherRef.current = null;
      bikeSummaryRef.current = null;
      machinesRef.current = null;
      setCoachState({
        ...INITIAL_COACH_STATE,
        countUpMode: !!plan?.countUpMode,
      });
      setSessionLoadedIntoCoach(true);
      setView("coach");
      syncCountInFromEngine();
    },
    [workoutPlan, isAuthenticated, setWorkoutPlan, setView, syncCountInFromEngine],
  );

  const loadSessionFromData = useCallback(
    (planData) => {
      if (!planData) return;
      // Normalise API-loaded / cross-device plans the same way the localStorage
      // mount path does (WorkoutContext): SetRest→Rest, auto-injected→Transition
      // coercion, and the structural ghost-rest demotion. Without this, plans
      // synced from another device resurface noisy inter-exercise rest rows.
      const plan = coerceLegacyNodeTypes(planData);
      // Drop any baked "history-guided" provenance: the pill describes a fresh
      // generation, not a session loaded from history (issue #9 / #26). The
      // Mixed-archetype pill goes with it for the same reason — it explains
      // what the generator chose and offers to reroll it, neither of which
      // means anything for a session being replayed from history.
      if (plan?.meta?.guidance || plan?.meta?.mixedArchetype) {
        plan.meta = { ...plan.meta };
        delete plan.meta.guidance;
        delete plan.meta.mixedArchetype;
      }
      setWorkoutPlan(plan);
      setSessionLoadedIntoCoach(false);
      setView("session");
    },
    [setWorkoutPlan, setView],
  );

  const clearLoadedSession = useCallback(() => {
    clearInterval(timerRef.current);
    clearInterval(countInTickRef.current);
    engineRef.current = null;
    sessionStartedAtRef.current = null;
    activeProgrammeSessionRef.current = null;
    phaseLogRef.current = [];
    hrSummaryRef.current = null;
    envRef.current = null;
    weatherRef.current = null;
    bikeSummaryRef.current = null;
    machinesRef.current = null;
    setCoachState({ ...INITIAL_COACH_STATE });
    setSessionLoadedIntoCoach(false);
    syncCountInFromEngine();
  }, [syncCountInFromEngine]);

  const clearSession = useCallback(() => {
    clearLoadedSession();
    setWorkoutPlan(null);
  }, [clearLoadedSession, setWorkoutPlan]);

  const setActiveProgrammeSession = useCallback((data) => {
    activeProgrammeSessionRef.current = data;
  }, []);

  // ---------------------------------------------------------------------------
  // Plan editing
  // ---------------------------------------------------------------------------

  // Plan-tree edits come from the shared editor so the programme session dialog
  // (#59) gets identical semantics by construction. Empty indexPath targets the
  // root — used to rename the session (issue #44).
  const {
    updatePlanNode,
    deletePlanNode,
    addPlanNode,
    movePlanNode,
    duplicatePlanNode,
    planDisplayProps,
  } = usePlanEditor(setWorkoutPlan);

  // Everything the mid-session load editor needs about the phase in progress,
  // read from the engine rather than re-derived in the view: which dimension is
  // prescribed, its current figure, and where this set sits in the exercise.
  //
  // The set count comes from the plan (enclosingSetCount) rather than the queue
  // item's enclosing group, because that is the number setNodeLoad will size a
  // per-set array to — reading it from anywhere else risks offering a "set 4 of
  // 5" the write path cannot honour.
  const getActiveLoadContext = useCallback(() => {
    const engine = engineRef.current;
    const item = engine?.getCurrentState()?.activeItem;
    if (!item || item.kind === "recovery" || item.isTransition) return null;

    const indexPath = treePathToIndexPath(item.sourceTreePath);
    if (indexPath.length === 0) return null;

    const authored = findNodeByPath(workoutPlan, item.sourceTreePath);
    const setCount = enclosingSetCount(workoutPlan, indexPath);
    const setIndex = Math.min(
      Math.max(0, item.enclosingGroup?.iter ?? 0),
      Math.max(0, setCount - 1),
    );
    const dim = nodeLoadDimension(item.node) || nodeLoadDimension(authored);
    const current = Number(item.node?.[dim]);

    return {
      label: item.node?.label || "",
      dim: dim || "weight",
      value: Number.isFinite(current) && current > 0 ? current : null,
      setIndex,
      setCount,
      // The edit target, carried with the snapshot so a write is aimed at the
      // phase the user was looking at — a timed phase can auto-advance while
      // the editor is open, and re-reading the active item at submit time would
      // silently move the figure onto the next exercise.
      sourceTreePath: item.sourceTreePath,
      indexPath,
    };
  }, [workoutPlan]);

  // Reps actually done (#97). Done records the prescribed reps; during the rest
  // that follows, the Coach offers the set just finished for correction, and
  // the completion screen offers every set (#144). The figure lands on its
  // phase-log entry as `actualReps`.
  const getLastRepsSet = useCallback(
    () => lastRepsSet(phaseLogRef.current),
    // repsEdits: a correction changes what this returns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repsEdits],
  );
  const getDoneRepsSets = useCallback(
    () => doneRepsSets(phaseLogRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repsEdits],
  );

  const amendSetReps = useCallback((index, reps) => {
    if (!amendReps(phaseLogRef.current, index, reps)) return false;
    setRepsEdits((v) => v + 1);
    return true;
  }, []);

  // Adjust the load on the phase in progress, mid-session (issue #76).
  //
  // Two writes, because the running session has two records of the figure and
  // they must not disagree: the compiled queue (what the Coach announces from
  // here on) and the plan tree (what gets saved as `plan_data` and read back by
  // the summary and history). The engine patch is what makes the change land on
  // the set you are standing in front of rather than the next time you run the
  // session.
  //
  // `scope` is the reach of the correction — see setNodeLoad. Defaults to
  // "onward": a load you change mid-session is nearly always one you keep for
  // the rest of the exercise, and it never rewrites a set already completed.
  //
  // `target` is the snapshot getActiveLoadContext handed the editor. Passing it
  // back is what keeps the write aimed at the phase that was on screen when the
  // dialog opened; without one, the phase in progress right now is used.
  const updateActiveLoad = useCallback(
    ({ dim = "weight", value, scope = "onward", target = null } = {}) => {
      const engine = engineRef.current;
      const item = engine?.getCurrentState()?.activeItem;
      if (!engine || !LOAD_DIMENSIONS.includes(dim)) return false;

      const parsed = value === null || value === "" ? null : Number(value);
      if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) {
        return false;
      }

      const sourceTreePath = target?.sourceTreePath || item?.sourceTreePath;
      if (!sourceTreePath) return false;
      const setIdx = target
        ? (target.setIndex ?? 0)
        : (item.enclosingGroup?.iter ?? 0);
      const indexPath = target?.indexPath || treePathToIndexPath(sourceTreePath);
      if (indexPath.length === 0) return false;

      // The plan tree is the record; write it first so a re-render can never
      // observe the queue holding a figure the plan has not accepted.
      setWorkoutPlan((prev) =>
        setNodeLoad(prev, indexPath, {
          dim,
          value: parsed,
          setIndex: setIdx,
          scope,
        }),
      );

      // Mirror onto the live queue. The opposite load dimension is cleared
      // (undefined deletes) because the schema lets only one of them take
      // effect — see setNodeLoad.
      const other = LOAD_DIMENSIONS.find((d) => d !== dim);
      engine.updateNodeFields(
        sourceTreePath,
        { [dim]: parsed ?? undefined, [other]: undefined },
        { setIdx: scope === "set" ? setIdx : null },
      );
      syncFromEngine();
      return true;
    },
    [setWorkoutPlan, syncFromEngine],
  );

  // Apply an edit made to the whole session mid-run (#77).
  //
  // The sibling of updateActiveLoad, and the distinction between them is the
  // point. A load edit only feeds an ESTIMATE, so it is patched onto the
  // compiled queue in place and the clock is left alone. A time edit IS the
  // clock, so the queue has to be rebuilt — otherwise the session keeps running
  // the duration you just corrected and only honours it next time.
  //
  // Meant for a paused session: the phase countdown lives in React state here,
  // not in the engine, so rebuilding under a running clock would move the
  // goalposts mid-phase. The Coach only offers the editor while paused.
  //
  // Order matters, and matches updateActiveLoad: the plan tree is the record,
  // so it is written first and the queue is brought into line with it.
  const applySessionEdit = useCallback(
    (nextPlan) => {
      if (!nextPlan || !Array.isArray(nextPlan.children)) return false;
      setWorkoutPlan(nextPlan);

      const engine = engineRef.current;
      if (!engine) return true;

      const { activeItem, durationChanged } = engine.updatePlan(nextPlan);
      // Pass the new tree explicitly — React has not committed it yet, so the
      // closure's `workoutPlan` is still the old one.
      syncFromEngine(nextPlan);

      // Rebase the phase clock onto the edited duration, keeping the time
      // already served. Shortening a 60s phase to 40s when 30s are gone leaves
      // 10s, not 40. syncFromEngine cannot do this itself: it holds
      // timeLeftInPhase steady whenever the phase identity is unchanged, which
      // is exactly the case here.
      //
      // Untouched when the phase in progress was DELETED — the engine has
      // reseated onto a different phase, so syncFromEngine has already seen a
      // phase change and started its clock from full.
      if (durationChanged) {
        setCoachState((prev) => {
          if (!activeItem?.isTimed) return { ...prev, timeLeftInPhase: 0 };
          const served = Math.max(
            0,
            prev.elapsedSessionTime - prev.phaseStartElapsed,
          );
          return {
            ...prev,
            timeLeftInPhase: Math.max(
              0,
              Math.round(activeItem.durationSec) - served,
            ),
          };
        });
      }
      return true;
    },
    [setWorkoutPlan, syncFromEngine],
  );

  // Where the engine is and how it compiled, for planning a trim to a finish-by
  // deadline (#8): proposeTrim recompiles candidate plans with the same options
  // so the savings it reports are the ones the session will actually see.
  const getTrimContext = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return null;
    return {
      queue: engine.queue,
      currentIndex: engine.currentIndex,
      compileOpts: {
        skipLastRest: engine.skipLastRest,
        profile: engine.profile,
        historyMap: engine.historyMap,
      },
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Upcoming exercises (for pause overview)
  // ---------------------------------------------------------------------------

  const getUpcomingExercises = useCallback(
    (limit = 6) => {
      const engine = engineRef.current;
      if (!engine) return [];
      const start = engine.currentIndex + 1;
      const slice = engine.queue.slice(start, start + limit);
      return slice.map((item) => ({
        label: item.node.label,
        value: item.node.value,
        unit: item.node.unit,
        type: item.node.type,
      }));
    },
    // engineRef is mutable; intentionally no deps so the callback stays stable.
    [],
  );

  return {
    // State
    coachState,
    setCoachState,
    sessionLoadedIntoCoach,
    setSessionLoadedIntoCoach,
    // Count-in (replaces useCountIn)
    countInRemaining,
    countInTotal,
    countInContext,
    // Refs
    timerRef,
    phaseLogRef,
    activeProgrammeSessionRef,
    hrSummaryRef,
    bikeSummaryRef,
    machinesRef,
    telemetryRef,
    telemetrySummaryRef,
    telemetrySessionIdRef,
    telemetryFinalizeRef,
    telemetryClearAfterSaveRef,
    telemetryPeekRef,
    engineRef,
    // Navigation
    startWorkout,
    finishWorkout,
    completeWorkout,
    keepGoing,
    savePartialSession,
    playPause,
    skipToNext,
    skipToPrevious,
    confirmReady,
    updateFreeLog,
    updateFreeChecklist,
    updateFreeRests,
    // Reps actually done (#97)
    getLastRepsSet,
    getDoneRepsSets,
    amendSetReps,
    skipRoundForward,
    skipRoundBack,
    getUpcomingExercises,
    getActiveLoadContext,
    updateActiveLoad,
    applySessionEdit,
    getTrimContext,
    // Session loading
    loadSessionIntoCoach,
    loadSessionFromData,
    clearLoadedSession,
    clearSession,
    setActiveProgrammeSession,
    // Plan editing
    updatePlanNode,
    deletePlanNode,
    addPlanNode,
    movePlanNode,
    duplicatePlanNode,
    planDisplayProps,
  };
};

export { INITIAL_COACH_STATE, getPhaseKey };
export default useWorkoutCoach;
